import moment from 'moment';
import _ from 'lodash';
import firebase from './firebase';
import {ADDONS, ADDONS_LIST, SERVICES} from './constants/servicesList';
import DATE_FORMAT from './constants/dateFormat';
import API_KEYS from './constants/apiKeys';


const isSameDay = (momentDate1, momentDate2) => {
    return momentDate1.year() === momentDate2.year() &&
        momentDate1.month() === momentDate2.month() &&
        momentDate1.date() === momentDate2.date();
};

export const createMomentFromDateAndTime = (dateMomentObj, timeMomentObj) => {
    const date = dateMomentObj.format('YYYY-MM-DD');
    const time = timeMomentObj.set('second', 0).format('HH:mm:ss');

    return moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm:ss');
};

export const getMonthDateRange = momentDate => {
    return {
        start: momentDate.startOf('month').toDate(),
        end: momentDate.endOf("month").toDate(),
    };
};

export const convertMomentToDate = unix => {
    return new Date(unix * 1000);
};

export const isToday = momentDate => {
    return isSameDay(moment(), momentDate);
};

export const convertTimestampToMoment = timestamp => {
    return moment(timestamp.toDate());
};

export const createTimestampFromMoment = momentObj => {
    return firebase.firestore.Timestamp.fromDate(convertMomentToDate(momentObj.unix()));
};

export const getDisabledDays = (monthDates = []) => {
    return monthDates
        .filter(day => _.has(day, 'holiday') && _.has(day, 'date_time'))
        .map(day => {
            const momentObj = convertTimestampToMoment(day.date_time);

            return momentObj.format('D');
        });
};

export const getDisabledHours = (date, monthDates = []) => {
    const unavailableTimeSlots = monthDates
        .filter(booking => {
            if (_.has(booking, 'holiday')) return false;

            return isSameDay(convertTimestampToMoment(booking.date_time), date)
        })
        .map(booking => calculateUnavailableTimeSlotsV2(booking));

    // Note: this array may contain duplicate timestamp values
    return _.flattenDeep(unavailableTimeSlots);
};

export const createBookingDateTime = (date, time)=> {
    const formattedDate  = date.format(DATE_FORMAT.STANDARD_DATE);
    const momentDate = moment(formattedDate + time, 'YYYY-MM-DDLT');

    return createTimestampFromMoment(momentDate);
};

export const isEligibleForRefund = (timestamp) => {
      const bookingDateTime = convertTimestampToMoment(timestamp);
      const duration = moment.duration(moment().diff(bookingDateTime));
      const hours = duration.asHours();

      return hours < 48;
};

export const isBookingInThePast = (timestamp) => {
    const bookingDateTime = convertTimestampToMoment(timestamp);

    return bookingDateTime <  moment();
};

/**
 * @deprecated
 */
export const calculateAddonsTotal = (addons = []) => {
    let total = 0;

    addons.forEach(addon => total += Number(addon.price));

    return convertNumberToCurrencyFormat(total);
};

/**
 * @deprecated
 */
export const calculateAddonsTotalV2 = (addons = []) => {
    return addons.reduce((acc, addon) => acc + (Number(addon.price) * addon.quantity), 0);
};

export const createAddonLabelsAndTotal = (addonLabels = [], valeuxWindowQuantity = 0) => {
    let total = 0;
    const labels = [];
    const { velux_roof_window } = ADDONS;

    addonLabels.forEach(label => {
         const { price } = ADDONS_LIST.find(item => item.label === label);

         labels.push(label);
         total += Number(price);
    });

    if (0 < valeuxWindowQuantity) {
        labels.push(`${velux_roof_window.label} x ${valeuxWindowQuantity}`);
        total += Number(velux_roof_window.price) * valeuxWindowQuantity;
    }

    return {
        labels,
        total,
    };
};

/**
 * @deprecated
 */
export const calculateUnavailableTimeSlots = (serviceGka, bookingDateTime) => {
    const selectedService = SERVICES[serviceGka];

    if (!selectedService) return [];

    const { duration } = selectedService;
    let i = 0;
    const timeSlotsTakenCount = (Number(duration) / 15) + 1; // e.g. 60 (mins) would take up 4 timeslots. + 1 to add 15 mins for travel
    const startTime = convertTimestampToMoment(bookingDateTime); // This is mutating within the loop. Change this!
    const unavailableTimeSlots = [startTime.format(DATE_FORMAT.BOOKING_TIME)];

    while (i < timeSlotsTakenCount) {
        unavailableTimeSlots.push(
            startTime
            .add(15, 'minute')
            .format(DATE_FORMAT.BOOKING_TIME)
        );

        i++;
    }

    return unavailableTimeSlots;
};

export const calculateUnavailableTimeSlotsV2 = (booking) => {
    let i = 1;

    const bookingDateTime = booking.date_time;
    const startTime = convertTimestampToMoment(bookingDateTime); // This is mutating within the loop(s). Change this!
    const unavailableTimeSlots = [startTime.format(DATE_FORMAT.BOOKING_TIME)];

    if (_.has(booking, 'end_time')) {
        const minutesDiff = convertTimestampToMoment(booking.end_time).diff(convertTimestampToMoment(booking.date_time), 'minutes');
        const maxIncrement = minutesDiff / 30; // e.g. 60 (mins) would take up 2 timeslots.

        while (i < maxIncrement) {
            unavailableTimeSlots.push(
                startTime
                    .add(30, 'minute')
                    .format(DATE_FORMAT.BOOKING_TIME)
            );

            i++;
        }

        return unavailableTimeSlots;
    }

    const serviceGka = booking.selected_service;
    const selectedService = SERVICES[serviceGka];

    if (!selectedService) return [];

    const { duration_plus_travel } = selectedService;
    const timeSlotsTakenCount = Number(duration_plus_travel) / 30; // e.g. 60 (mins) would take up 2 timeslots.

    while (i < timeSlotsTakenCount) {
        unavailableTimeSlots.push(
            startTime
                .add(30, 'minute')
                .format(DATE_FORMAT.BOOKING_TIME)
            );

        i++;
    }

    return unavailableTimeSlots;
};

export const createDashboardRowData = (row) => {
    function getTimestampFromRecord(key) {
        const timestampValue = _.get(row, key);

        return timestampValue ? convertTimestampToMoment(timestampValue).format('lll') : null;
    }

    function getSelectedAddons() {
        const addons = _.get(row, 'selected_addons', []);

        return addons.length ? addons.join(', ') : 'N/A';
    }

    return {
        contact_number: row.contact_number,
        email_address: row.email_address,
        selected_addons: getSelectedAddons(),
        total_charge: row.total_charge ? `£${row.total_charge.toFixed(2)}` : null,
        property_type: row.property_type,
        has_parking_available: row.has_parking_available === true ? 'Yes' : 'No',
        has_access_to_back_of_property: row.has_access_to_back_of_property === true ? 'Yes' : 'No',
        previous_date_time: getTimestampFromRecord('previous_date_time'),
        cancelled_at: getTimestampFromRecord('cancelled_at'),
        eligible_for_refund: row.eligible_for_refund === true ? 'True' : 'False',
        additional_comments: _.get(row, 'additional_comments', null),
        end_time: getTimestampFromRecord('end_time'),
    };
};

const convertNumberToCurrencyFormat = (value = 0) => {
    let valueToConvert = typeof value === 'string' ? Number(value) : value;

    return Number(valueToConvert.toFixed(2));
};

export const calculateChargeTotal = (serviceAndAddonsCharge, bookingCharge) => {
    const totalCharge = Number(serviceAndAddonsCharge) + Number(bookingCharge);

    return convertNumberToCurrencyFormat(totalCharge);
};

export const calculateBookingCharge = (serviceAndAddonsCharge) => {
    const chargePlusStripeFee = (serviceAndAddonsCharge * 0.014) + 0.2;

    return convertNumberToCurrencyFormat(chargePlusStripeFee);
};

export const calculateCharges = (formValues) => {
    const serviceCharge = convertNumberToCurrencyFormat(formValues.price);
    const addonsCharge = convertNumberToCurrencyFormat(formValues.addonsCharge);
    const serviceAndAddonsCharge = serviceCharge + addonsCharge;
    const bookingCharge = calculateBookingCharge(serviceAndAddonsCharge);
    const totalCharge = calculateChargeTotal(serviceAndAddonsCharge, bookingCharge);

    return {
        service_charge: serviceCharge,
        addons_charge: addonsCharge,
        booking_charge: bookingCharge,
        total_charge: totalCharge,
        stripe_total_charge: Math.round(totalCharge * 100),
    };
};

export const calculateRefundAmount = (bookingData) => {
    const { total_charge, booking_charge } = bookingData;
    const refundAmount = (total_charge * 100) - (booking_charge * 100);

    return Math.round(refundAmount);
};

export const generateUuid = (formValues) => {
    const { bookingDateTime, fullName } = formValues;

    const momentObj = convertTimestampToMoment(bookingDateTime);
    const a = fullName.charAt(0);
    const b = fullName.charAt(fullName.length - 1);

    return `${a}${b}-${momentObj.unix()}-${moment().unix()}`.toUpperCase();
};

export const createBookingData = formValues => {
    const {
        usePropertyAddressForBilling,
        addressLine1,
        city,
        postcode,
    } = formValues;
    const charges = calculateCharges(formValues);

    return {
        uuid: generateUuid(formValues),

        full_name: formValues.fullName,
        contact_number: formValues.contactNumber,
        email_address: formValues.emailAddress,
        date_time: formValues.bookingDateTime,

        selected_service: formValues.gka,
        selected_addons: formValues.addonsList,

        address_line_1: addressLine1,
        city: city,
        postcode: postcode,

        billing_address_line_1: usePropertyAddressForBilling ? addressLine1 : formValues.billingAddressLine1,
        billing_city: usePropertyAddressForBilling ? city : formValues.billingAddressLine1,
        billing_postcode: usePropertyAddressForBilling ? postcode : formValues.billingPostcode,

        property_type: formValues.propertyType,
        has_parking_available: formValues.isParkingAvailable || false,
        has_access_to_back_of_property: formValues.hasAccessToBackOfProperty || false,
        additional_comments: formValues.additionalComments || '',

        created_at: createTimestampFromMoment(moment()),
        updated_at: createTimestampFromMoment(moment()),
        booking_cancelled: false,
        booking_amended: false,

        email_booking_error: false,
        email_amended_error: false,

        ...charges,
    };
};

export const createManualBookingData = (formValues) => {
    const data = {
        uuid: generateUuid({ ...formValues, bookingDateTime: formValues.startTime }),

        full_name: formValues.fullName,
        manual_booking: true,

        date_time: createTimestampFromMoment(formValues.startTime),
        end_time: createTimestampFromMoment(formValues.endTime),
        selected_service: 'N/A (manual booking)',
    };

    if (formValues.contactNumber) {
        data.contact_number = formValues.contactNumber;
    }

    if (formValues.emailAddress) {
        data.email_address = formValues.emailAddress;
    }

    if (formValues.addressLine1) {
        data.address_line_1 = formValues.addressLine1;
    }

    if (formValues.city) {
        data.city = formValues.city;
    }

    if (formValues.postcode) {
        data.postcode = formValues.postcode;
    }

    return data;
};

export const createAmendedBookingData = (existingDateTime, newDateTime) => {
    return {
        date_time: newDateTime,
        previous_date_time: existingDateTime,

        updated_at: createTimestampFromMoment(moment()),
        booking_amended: true,
    };
};

export const createCancelledBookingData = (eligibleForRefund) => {
    return {
        cancelled_at: createTimestampFromMoment(moment()),
        eligible_for_refund: eligibleForRefund,

        updated_at: createTimestampFromMoment(moment()),
        booking_cancelled: true,
    };
};

export const createDateTimeBookingData = (bookingData) => {
    const data = {
        date_time: bookingData.date_time,
        selected_service: bookingData.selected_service,
    };

    if (bookingData.end_time) {
        data.end_time = bookingData.end_time;
    }

    return data;
};

export const createAmendedDateTimeBookingData = (amendedBookingData) => {
    return {
        date_time: amendedBookingData.date_time,
    };
};

export const createNewHolidayData = (dateTime) => {
    return {
        date_time: createTimestampFromMoment(dateTime),
        holiday: true,
    };
};

export const isProduction = () => {
    return API_KEYS.PRODUCTION === 'true';
};

export const isMaintenanceMode = () => {
    return API_KEYS.MAINTENANCE_MODE === 'true';
};
